/**
 * Hook für TanStack Query mit IndexedDB Fallback (Offline-Support)
 * Lädt normal online, fällt auf IndexedDB zurück wenn offline
 */

import { useQuery, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { useOnlineStatus } from './useOnlineStatus';
import { getOfflineDB } from '../lib/offlineDB';

type CacheKey = (string | number)[];

interface CachedQueryOptions<T> extends Omit<UseQueryOptions<T, Error, T>, 'queryFn'> {
  /**
   * TanStack Query key
   */
  queryKey: CacheKey;

  /**
   * Query function - called when online
   */
  queryFn: () => Promise<T>;

  /**
   * Fallback: Offline data loader
   * Wird aufgerufen wenn offline
   */
  getOfflineData?: () => Promise<T | undefined>;

  /**
   * Fallback: Cached data is stale after X ms (default: 5 min)
   */
  offlineCacheTtlMs?: number;

  /**
   * Debugging: Log cache hits/misses
   */
  debug?: boolean;
}

/**
 * Hook für Queries mit Offline-Support
 *
 * Beispiel:
 * ```
 * const { data: tickets } = useCachedQuery<TicketType[]>({
 *   queryKey: ['tickets', userId],
 *   queryFn: () => api.getMyTickets(),
 *   getOfflineData: () => getOfflineDB().getAllTickets(),
 * });
 * ```
 */
export function useCachedQuery<T>({
  queryKey,
  queryFn,
  getOfflineData,
  offlineCacheTtlMs = 5 * 60 * 1000, // 5 Minuten default
  debug = false,
  ...options
}: CachedQueryOptions<T>): UseQueryResult<T, Error> {
  const { isOnline } = useOnlineStatus();
  const cacheKey = queryKey.join(':');
  const localStorageKey = `cache:${cacheKey}`;

  const wrappedQueryFn = async (): Promise<T> => {
    if (isOnline) {
      try {
        const result = await queryFn();

        // Cache-Erfolg lokal speichern für offline
        if (result) {
          const cacheData = {
            data: result,
            timestamp: Date.now(),
          };
          localStorage.setItem(localStorageKey, JSON.stringify(cacheData));
          if (debug) {
            console.log(`[useCachedQuery] Cached online data for: ${cacheKey}`);
          }
        }

        return result;
      } catch (error) {
        // Netzwerkfehler - versuche Fallback
        if (debug) {
          console.log(`[useCachedQuery] Online query failed, trying fallback for: ${cacheKey}`);
        }

        // Versuche offline data loader
        if (getOfflineData) {
          const offlineResult = await getOfflineData();
          if (offlineResult) {
            return offlineResult;
          }
        }

        // Versuche lokal gecachte Daten
        const cached = localStorage.getItem(localStorageKey);
        if (cached) {
          const { data, timestamp } = JSON.parse(cached);
          const age = Date.now() - timestamp;
          if (age < offlineCacheTtlMs) {
            if (debug) {
              console.log(`[useCachedQuery] Using local cache for: ${cacheKey} (age: ${age}ms)`);
            }
            return data;
          }
        }

        throw error; // Re-throw wenn kein fallback
      }
    } else {
      // Offline Mode
      if (debug) {
        console.log(`[useCachedQuery] Offline, using fallback for: ${cacheKey}`);
      }

      // 1. Versuche offline data loader (z.B. IndexedDB)
      if (getOfflineData) {
        const offlineResult = await getOfflineData();
        if (offlineResult) {
          if (debug) {
            console.log(`[useCachedQuery] Loaded offline data from loader: ${cacheKey}`);
          }
          return offlineResult;
        }
      }

      // 2. Fallback zu localStorage Cache
      const cached = localStorage.getItem(localStorageKey);
      if (cached) {
        const { data } = JSON.parse(cached);
        if (debug) {
          console.log(`[useCachedQuery] Using cached data from localStorage: ${cacheKey}`);
        }
        return data;
      }

      // 3. Kein Cache verfügbar
      throw new Error(`No offline data available for ${cacheKey}`);
    }
  };

  return useQuery({
    ...options,
    // isOnline an den Key hängen, damit der Online→Offline→Online-Wechsel die Query
    // neu ausführt (sonst hält wrappedQueryFn den alten isOnline-Wert in der Closure
    // fest und refetcht beim Reconnect nicht). Invalidierung per Prefix bleibt intakt;
    // der localStorage-Cache-Key (oben) ist bewusst OHNE isOnline → Offline-Fallback
    // liest weiter den online befüllten Cache.
    queryKey: [...queryKey, isOnline ? '__online' : '__offline'],
    queryFn: wrappedQueryFn,
    // Im offline mode: Keep daten länger (bis online)
    staleTime: isOnline ? (options.staleTime ?? 5 * 60 * 1000) : 1000 * 60 * 60 * 24, // 24h offline
    gcTime: isOnline ? (options.gcTime ?? 10 * 60 * 1000) : 1000 * 60 * 60 * 24, // 24h offline
  });
}

/**
 * Hook für gecachte Queries von Submissions (Offline-Draft speicherbar)
 */
export function useCachedSubmissionsQuery<T>(
  queryKey: CacheKey,
  queryFn: () => Promise<T>,
  options?: Omit<CachedQueryOptions<T>, 'queryKey' | 'queryFn' | 'getOfflineData'>
) {
  return useCachedQuery<T>({
    queryKey,
    queryFn,
    getOfflineData: async () => {
      // Submissions from IndexedDB
      const db = getOfflineDB();
      const submissions = await db.getAllSubmissions();
      return submissions as unknown as T;
    },
    ...options,
  });
}

/**
 * Hook für gecachte Ticket-Queries
 */
export function useCachedTicketsQuery(
  queryKey: CacheKey,
  queryFn: () => Promise<any[]>,
  options?: Omit<CachedQueryOptions<any[]>, 'queryKey' | 'queryFn' | 'getOfflineData'>
) {
  return useCachedQuery<any[]>({
    queryKey,
    queryFn,
    getOfflineData: async () => {
      const db = getOfflineDB();
      return db.getAllTickets();
    },
    ...options,
  });
}

/**
 * Hook für gecachte Customer-Queries
 */
export function useCachedCustomersQuery(
  queryKey: CacheKey,
  queryFn: () => Promise<any[]>,
  options?: Omit<CachedQueryOptions<any[]>, 'queryKey' | 'queryFn' | 'getOfflineData'>
) {
  return useCachedQuery<any[]>({
    queryKey,
    queryFn,
    getOfflineData: async () => {
      const db = getOfflineDB();
      return db.getAllCustomers();
    },
    ...options,
  });
}
